import { Typography, Grid, TableRow, TableCell, useTheme, TableBody, Divider } from "@mui/material";
import React from "react";
import { useState, useEffect, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import AddEditBankAccountMutation from "./AddEditBankAccountMutation/AddEditBankAccountMutation";
import { State } from "./interfaces";
import { Container, StyledTable, StyledTableCell, Label } from "./styles";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { EmployeeBankAccountSTO } from "../../../models/employee-bank-accounts/employee-bank-account-sto";
import { EmployeeBankAccountMutationSTO } from "../../../models/employee-bank-accounts/mutations/employee-bank-account-mutation-sto";
import { MutationStatusRef } from "../../../models/mutation-statuses/mutation-status-ref";
import AddEditItem from "../../../reusable-components/Table/AddEditItem/AddEditItem";
import { FormField } from "../../../reusable-components/Table/AddEditItem/interfaces";

import { CurrenciesApiService } from "../../../services/currencies/currencies-api.service";
import { loadCurrentUserEmployeesBankAccounts as loadEmployeesBankAccounts } from "../../../services/employees/employees-bank-accounts-api.service";
import { EmployeesBankAccountsApiService } from "../../../services/employees/employees-bank-accounts-api.service";
import { loadEmployeeCurrentBankAccountMutations } from "../../../services/employees/employees-current-bank-account-mutations-api.service";

import { MUTATIONS_STATUSES } from "../../../utils/constants/constants";

import { EmployeeDataContext } from "../EmployeePersonalData";

const EmployeeBankAccount = () => {
  const { t } = useTranslation();
  const tRP = "components.data_and_documents"; // tRP - translation root path
  const theme = useTheme();
  const { employee, forManager } = useContext(EmployeeDataContext);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    bankAccount: null,
    mutation: null,
    hasMutation: false,
  });

  const getEmployeeBankAccount = useCallback(
    async (employeeId: string, signal?: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const employeesBankAccountsResponse = await loadEmployeesBankAccounts(employeeId, forManager, signal);

        const employeeBankAccountsMutationsResponse = await loadEmployeeCurrentBankAccountMutations(
          employeeId,
          forManager,
          signal
        );

        const bankAccount = employeesBankAccountsResponse.items[0] ?? null;

        const newEmployeeBankAccountMutation: EmployeeBankAccountMutationSTO = {
          isDeleted: false,
          id: uuidv4(),
          companyAccount: null,
          legalEntity: employee?.legalEntity ?? null,
          employee,
          original: bankAccount,
          name: bankAccount?.name,
          iban: bankAccount?.iban,
          currency: bankAccount?.currency,
          description: bankAccount?.description,
          status: {
            id: MUTATIONS_STATUSES.PENDING.id,
          } as MutationStatusRef,
          toDate: bankAccount?.toDate,
        };

        if (!signal || !signal.aborted) {
          setState((state) => ({
            ...state,
            bankAccount,
            hasMutation: !!employeeBankAccountsMutationsResponse.items.length,
            mutation: employeeBankAccountsMutationsResponse.items[0] ?? newEmployeeBankAccountMutation,
          }));
        }
      } catch (error) {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, error }));
      } finally {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, loading: false }));
      }
    },
    [employee]
  );

  useEffect(() => {
    const abortController = new AbortController();

    if (employee?.id) getEmployeeBankAccount(employee.id, abortController.signal);

    return () => abortController.abort();
  }, [employee?.id, getEmployeeBankAccount]);

  const newBankAccount: EmployeeBankAccountSTO = {
    id: uuidv4(),
    isDeleted: false,
    employee,
    companyAccount: employee?.companyAccount ?? null,
    name: "",
    description: "",
    iban: "",
    currency: null,
    toDate: new Date(9999, 11, 31),
  };

  const formFields: FormField[] = [
    {
      field: "name",
      labelKey: t(`${tRP}.employee_data.employee_bank_account.fields.name`),
    },
    {
      field: "description",
      labelKey: t(`${tRP}.employee_data.employee_bank_account.fields.description`),
    },
    {
      field: "iban",
      labelKey: t(`${tRP}.employee_data.employee_bank_account.fields.iban`),
    },
    {
      field: "currency",
      labelKey: t(`${tRP}.employee_data.employee_bank_account.fields.currency`),
      apiService: new CurrenciesApiService(),
      fieldType: "dropdown",
    },
    {
      field: "toDate",
      labelKey: t(`${tRP}.employee_data.employee_bank_account.fields.toDate`),
      // type: "date",
      fieldType: "datePicker",
    },
  ];

  const { bankAccount, mutation, hasMutation } = state;

  return (
    <Container>
      <LoadingOverlay loading={state.loading}>
        <StyledTable>
          <TableBody>
            <TableRow>
              <TableCell
                rowSpan={6}
                style={{ verticalAlign: "top", padding: 0, width: "160px" }}
              >
                <Grid
                  style={{
                    borderRadius: "5px",
                    backgroundColor: theme.palette.background.paper,
                    border: "none",
                    padding: "0px 5px",
                    height: "45px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant={"h6"}>{t(`${tRP}.employee_data.employee_bank_account.title`)}</Typography>
                  {bankAccount && (
                    <AddEditBankAccountMutation
                      original={bankAccount}
                      mutation={mutation}
                      hasMutation={hasMutation}
                      handleReload={() => getEmployeeBankAccount(employee?.id ?? "")}
                    />
                  )}
                </Grid>
              </TableCell>

              <TableCell
                rowSpan={6}
                style={{ verticalAlign: "top", padding: 0 }}
              >
                <Divider
                  flexItem
                  orientation="horizontal"
                  style={{
                    width: "100%",
                    minWidth: "10px",
                    marginTop: "20px",
                    border: `3px solid ${theme.palette.background.paper}`,
                  }}
                />
              </TableCell>

              <StyledTableCell>
                {bankAccount ? (
                  <>
                    <Label variant="body2">{`${t(`${tRP}.employee_data.employee_bank_account.fields.name`)}`}</Label>
                    <>{`${bankAccount.name}`}</>
                  </>
                ) : (
                  <AddEditItem
                    titleForEditComponent={`${t(`${tRP}.employee_data.employee_bank_account.add_title`)}`}
                    item={newBankAccount}
                    formFields={formFields}
                    serviceApiInstance={new EmployeesBankAccountsApiService()}
                    handleReload={() => getEmployeeBankAccount(employee?.id ?? "")}
                    customAddEditButtonStyle={{
                      width: "unset",
                      height: "unset",
                      padding: "8px",
                      color: theme.palette.primary.main,
                      backgroundColor: "transparent",
                      borderRadius: theme.shape.borderRadius,
                    }}
                  >
                    <Typography>{t(`${tRP}.employee_data.employee_bank_account.add_title`)}</Typography>
                  </AddEditItem>
                )}
              </StyledTableCell>
            </TableRow>
            {bankAccount && (
              <>
                <TableRow>
                  <StyledTableCell>
                    <Label variant="body2">IBAN</Label>
                    {bankAccount?.iban}
                  </StyledTableCell>
                </TableRow>

                <TableRow>
                  <StyledTableCell>
                    <Label variant="body2">Currency</Label>
                    {bankAccount?.currency?.name}
                  </StyledTableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </StyledTable>
      </LoadingOverlay>
    </Container>
  );
};

export default EmployeeBankAccount;
